import { useEffect, useState } from 'react'
import { BellRing, Check, Save, Smartphone, Trash2 } from 'lucide-react'
import { Button, FormError, LoadFailure, Modal, Switch, Skeleton } from '../../core/index.js'
import { useStaffNotificationActions, useStaffNotifications } from '../hooks.js'
import { STAFF_ALERT_CONSENT_WORDING, type StaffAlertKind, type StaffNotificationSettingsInput } from '../types.js'

const KINDS: readonly { key: StaffAlertKind; label: string; detail: string; when: string }[] = [
  { key: 'urgent', label: 'Needs you · urgent', detail: 'A shift starting within hours with a seat nobody took; a callout nobody answered.', when: 'At once' },
  { key: 'approvals', label: 'Needs you · approvals and proposals', detail: 'A swap to approve; a census change the engine left to you.', when: 'At once' },
  { key: 'digest', label: 'Daily digest', detail: 'What waits for you and what the engine did in the last day.', when: 'Daily' },
  { key: 'beforeRelease', label: 'Before the release', detail: 'The month the engine is about to release, and anything holding it.', when: '24 h ahead' },
  { key: 'weekly', label: 'Weekly summary', detail: 'The week in a line.', when: 'Mondays 07:00' },
]

const TIME = 'w-24 rounded border border-marine/30 bg-white px-2 py-1 text-sm font-bold text-ink'
const FIELD = 'w-44 rounded border border-marine/30 bg-white px-2 py-1 text-sm font-bold text-ink'

/**
 * Your notifications (facility operations dashboard plan §5, D3): a staff member's own say on how they hear about
 * what needs them - their login email, a mobile they verify with a code and agree to, a text and an email switch per
 * kind of alert with the digest hour, and quiet hours for texts (urgent goes through). Opened from the header.
 */
export function StaffNotificationsModal({ onClose }: { onClose: () => void }) {
  const query = useStaffNotifications()
  const actions = useStaffNotificationActions()
  const [draft, setDraft] = useState<StaffNotificationSettingsInput | null>(null)
  const [mobile, setMobile] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [code, setCode] = useState('')
  useEffect(() => {
    if (query.data && draft === null) {
      const { kinds, digestAt, quietHours, facilityIds, briefingEmail } = query.data
      setDraft({ kinds, digestAt, quietHours, facilityIds, briefingEmail: briefingEmail ?? false })
    }
  }, [query.data, draft])

  const settings = query.data
  const failure = actions.save.error ?? actions.addMobile.error ?? actions.verify.error ?? actions.removeMobile.error
  const canText = settings?.mobile?.verified === true && !settings.mobile.optedOut
  const setKind = (key: StaffAlertKind, channel: 'sms' | 'email', on: boolean) => setDraft((current) => (current ? { ...current, kinds: { ...current.kinds, [key]: { ...current.kinds[key], [channel]: on } } } : current))

  return (
    <Modal
      eyebrow="Your notifications"
      title="How you hear about what needs you"
      subtitle="Yours alone. Your facility's defaults are on Configuration › Automation."
      onClose={onClose}
      footer={
        <div className="flex items-center gap-3">
          <Button icon={Save} disabled={!draft || actions.save.isPending} onClick={() => draft && actions.save.mutate(draft)}>
            {actions.save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {actions.save.isSuccess && <span className="text-xs font-bold text-green">Saved</span>}
        </div>
      }
    >
      {query.isError ? (
        <LoadFailure className="px-6 py-4" size="xs" message="Could not load your notification settings." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !settings || !draft ? (
        <Skeleton className="px-6 py-4" />
      ) : (
        <div className="flex flex-col gap-5 px-6 py-4">
          <section className="flex flex-col gap-2">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">How to reach you</h3>
            <div className="text-xs text-ink">
              <span className="font-bold">Email</span> · {settings.email ?? 'none on your login'} <span className="text-ink-mid">(your login, always on)</span>
            </div>
            {settings.mobile?.verified ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink">
                <Smartphone className="h-4 w-4 text-marine" aria-hidden="true" />
                <span className="font-bold">{settings.mobile.masked}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${settings.mobile.optedOut ? 'bg-coral-lt text-coral' : 'bg-green-lt text-green'}`}>{settings.mobile.optedOut ? 'You replied STOP' : 'Verified'}</span>
                <Button icon={Trash2} size="sm" variant="secondary" disabled={actions.removeMobile.isPending} onClick={() => actions.removeMobile.mutate()}>
                  Remove
                </Button>
              </div>
            ) : settings.mobile ? (
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  actions.verify.mutate(code.trim())
                }}
              >
                <span className="text-xs text-ink">We texted a code to {settings.mobile.masked}.</span>
                <input className={TIME} inputMode="numeric" maxLength={6} aria-label="The code" placeholder="000000" value={code} onChange={(event) => setCode(event.target.value)} />
                <Button type="submit" icon={Check} size="sm" disabled={code.trim().length !== 6 || actions.verify.isPending}>
                  Verify
                </Button>
              </form>
            ) : (
              <form
                className="flex flex-col gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  actions.addMobile.mutate({ mobile, wording: STAFF_ALERT_CONSENT_WORDING })
                }}
              >
                <label className="flex flex-wrap items-center gap-2 text-xs font-bold text-ink">
                  Mobile
                  <input className={FIELD} inputMode="tel" placeholder="(206) 555-0100" value={mobile} onChange={(event) => setMobile(event.target.value)} />
                </label>
                <label className="flex items-start gap-2 text-[11px] leading-relaxed text-ink-mid">
                  <input type="checkbox" className="mt-0.5" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
                  {STAFF_ALERT_CONSENT_WORDING}
                </label>
                <div>
                  <Button type="submit" icon={Smartphone} size="sm" disabled={!agreed || mobile.trim() === '' || actions.addMobile.isPending}>
                    Text me a code
                  </Button>
                </div>
              </form>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">What you are told, and how</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] text-xs">
                <thead>
                  <tr className="bg-marine-dk text-left text-[10px] font-bold uppercase tracking-wide text-white/70">
                    <th className="px-2 py-1.5">What</th>
                    <th className="px-2 py-1.5">Text</th>
                    <th className="px-2 py-1.5">Email</th>
                    <th className="px-2 py-1.5">When</th>
                  </tr>
                </thead>
                <tbody>
                  {KINDS.map((kind) => (
                    <tr key={kind.key} className="border-b border-marine/10">
                      <td className="px-2 py-2">
                        <span className="font-bold text-ink">{kind.label}</span>
                        <span className="block text-[10px] text-ink-mid">{kind.detail}</span>
                      </td>
                      <td className="px-2 py-2">
                        <Switch checked={draft.kinds[kind.key].sms} onChange={(on) => setKind(kind.key, 'sms', on)} label={`${kind.label} by text`} disabled={!canText} />
                      </td>
                      <td className="px-2 py-2">
                        <Switch checked={draft.kinds[kind.key].email} onChange={(on) => setKind(kind.key, 'email', on)} label={`${kind.label} by email`} disabled={!settings.email} />
                      </td>
                      <td className="px-2 py-2 text-ink-mid">
                        {kind.key === 'digest' ? <input type="time" className={TIME} aria-label="Digest time" value={draft.digestAt} onChange={(event) => setDraft({ ...draft, digestAt: event.target.value })} /> : kind.when}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-b border-marine/10">
                    <td className="px-2 py-2">
                      <span className="font-bold text-ink">AI briefing</span>
                      <span className="block text-[10px] text-ink-mid">Each briefing you press for, emailed to you as soon as it is ready (always, not only when you press Email me this).</span>
                    </td>
                    <td className="px-2 py-2 text-ink-mid">—</td>
                    <td className="px-2 py-2">
                      <Switch checked={draft.briefingEmail ?? false} onChange={(on) => setDraft({ ...draft, briefingEmail: on })} label="Always email my briefing" disabled={!settings.email} />
                    </td>
                    <td className="px-2 py-2 text-ink-mid">When ready</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!canText && <p className="text-[11px] text-ink-mid">Texts switch on once your mobile is verified.</p>}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-marine">
              <BellRing className="h-3.5 w-3.5" aria-hidden="true" /> Quiet hours for texts
            </h3>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Switch checked={draft.quietHours !== null} onChange={(on) => setDraft({ ...draft, quietHours: on ? { start: '21:00', end: '06:00' } : null })} label="Quiet hours" />
              {draft.quietHours && (
                <>
                  <label className="flex items-center gap-1 font-bold text-ink">
                    From <input type="time" className={TIME} value={draft.quietHours.start} onChange={(event) => setDraft({ ...draft, quietHours: { start: event.target.value, end: draft.quietHours?.end ?? '06:00' } })} />
                  </label>
                  <label className="flex items-center gap-1 font-bold text-ink">
                    To <input type="time" className={TIME} value={draft.quietHours.end} onChange={(event) => setDraft({ ...draft, quietHours: { start: draft.quietHours?.start ?? '21:00', end: event.target.value } })} />
                  </label>
                </>
              )}
              <span className="text-[11px] text-ink-mid">in the facility’s time zone; urgent items go through, everything else waits for email</span>
            </div>
          </section>
          <FormError error={failure} fallback="That did not go through. Try again." />
        </div>
      )}
    </Modal>
  )
}
